import { ApprovedOpenReservationFilter, Reservation, Table, TableCombination } from '@backend/domain'
import {
	ReservationRepository,
	SettingsRepository,
	TableCombinationRepository,
	TableRepository,
} from '@backend/repository'
import { ITime, ReservationState } from '@core/types'
import { getDaySchedule, removeNulls, TimeUtils } from '@core/utils'
import { TZDate } from '@date-fns/tz'
import { Injectable } from '@nestjs/common'
import { addDays, addMinutes, areIntervalsOverlapping, isBefore, isEqual, set, subDays } from 'date-fns'
import { flatMap, uniqBy } from 'es-toolkit'
import { In } from 'typeorm'
import { TimingRule } from '../../settings'
import { SlotOccupancy, TimeSlot } from '../types'
import { getAllPartySizes, getOpenTableCount } from '../utils'

/** Fine 15-minute grid during the busy booking window, 30 minutes afterwards. */
const FINE_STEP_MINUTES = 15
const COARSE_STEP_MINUTES = 30
/** Local restaurant time after which the coarse grid starts (20:15). */
const COARSE_GRID_FROM = { hours: 20, minutes: 15 }
/** Overnight shifts stay on the coarse grid until this local morning hour. */
const COARSE_GRID_UNTIL_HOUR = 6
/** Guests need a small runway — never offer a slot starting sooner than this. */
const MIN_LEAD_MINUTES = 15
/** Share of occupied tables from which a slot counts as almost full / in demand. */
const HIGH_OCCUPANCY_RATIO = 0.75
const MEDIUM_OCCUPANCY_RATIO = 0.35
/**
 * "Popular time" is relative to the restaurant's OWN history: a slot's demand
 * score (unique booked tables, summed over the same weekday+time of the past
 * weeks) must reach three quarters of the day's peak score. Real past
 * bookings only — a claim about history, never about today's availability.
 */
const POPULAR_LOOKBACK_WEEKS = 8
/** Minimum real evidence (booked table-weeks) — a single stray booking is never crowned. */
const POPULAR_MIN_TABLE_WEEKS = 3
/** Popular = score >= 3/4 of the day's peak (integer arithmetic keeps the boundary exact). */
const POPULAR_PEAK_SHARE_NUMERATOR = 3
const POPULAR_PEAK_SHARE_DENOMINATOR = 4
/**
 * House tip ("Geheimtipp"): per shift, two quiet bookable slots inside the
 * 30–90-minute window after opening carry a recommendation — an honest
 * preference statement that steers guests early and spreads the load. Which
 * two is picked deterministically from the calendar date, so the tip varies
 * from day to day (never looks static) but is stable across reloads.
 */
const RECOMMENDED_PER_SHIFT = 2
const TIP_WINDOW_START_MINUTES = 30
const TIP_WINDOW_END_MINUTES = 90
/** States that count as real historical demand (rejected/unconfirmed never do). */
const HISTORICAL_DEMAND_STATES = [ReservationState.approved, ReservationState.seated, ReservationState.done]

type TimeSlotOptions = {
	/** When set, slots are only reported available if this party size can actually be seated. */
	partySize?: number
	/** Reservation to ignore when computing occupancy (used when a guest moves their own reservation). */
	excludeReservationId?: string
	/** Compute the "popular time" signal (guest-facing paths only — the admin calendar never reads it). */
	includePopular?: boolean
}

@Injectable()
export class TimeslotService {
	constructor(
		private readonly _tableRepository: TableRepository,
		private readonly _settingsRepository: SettingsRepository,
		private readonly _reservationRepository: ReservationRepository,
		private readonly _tableCombinationRepository: TableCombinationRepository,
	) {}

	/**
	 * Generates a list of time slots with their availabity status
	 * @param date The date for which time slots will be generated
	 * @param timezone Optional timezone to convert timeslots to local time
	 * @param options Optional party-size aware filtering, see {@link TimeSlotOptions}
	 * @returns A list of {@link TimeSlot}
	 */
	async getTimeSlotsByDate(date: Date, timezone?: string, options?: TimeSlotOptions): Promise<TimeSlot[]> {
		const settings = await this._settingsRepository.findOne({ select: { schedule: {}, restaurant: {}, reservation: {} } })
		const schedule = settings?.schedule
		const restaurantTimezone = settings?.restaurant?.timezone
		const lastReservationTime = settings?.reservation?.lastReservationTime ?? undefined

		// Clients send local midnight (e.g. Berlin midnight = 22:00 UTC of the
		// PREVIOUS day). Normalize to the calendar day the guest actually meant,
		// in their timezone, so slots are built on the right day.
		date = this.normalizeToCalendarDay(date, timezone)

		const daySchedule = getDaySchedule(date, schedule)
		if (daySchedule.closed) return []

		// Find open reservations for the same day
		let reservations = await this._reservationRepository.findByDay(date, {
			where: { state: In(ApprovedOpenReservationFilter) },
			relations: { tables: true },
		})

		if (options?.excludeReservationId) {
			reservations = reservations.filter((reservation) => reservation.id !== options.excludeReservationId)
		}

		// Real past bookings for the "popular time" signal — one range query,
		// matched per slot against the same weekday+time of previous weeks.
		// The upper bound is clamped to NOW: when guests book far ahead, the
		// lookback windows anchored to the target day reach into the future, and
		// confirmed-but-not-yet-happened bookings must never count as history.
		const now = new Date()
		const historyEnd = isBefore(now, date) ? now : date
		const history = options?.includePopular
			? await this._reservationRepository.findByInterval(subDays(date, POPULAR_LOOKBACK_WEEKS * 7), historyEnd, {
					where: { state: In(HISTORICAL_DEMAND_STATES) },
					relations: { tables: true },
				})
			: []

		const tables = await this._tableRepository.find()

		// Combinations are only relevant when the party exceeds every single table
		const maxSingleCapacity = tables.length > 0 ? Math.max(...tables.map((table) => table.capacity)) : 0
		const combinations =
			options?.partySize && options.partySize > maxSingleCapacity
				? await this._tableCombinationRepository.find({ relations: { tables: true } })
				: []

		return this.generateTimeSlots(
			date,
			daySchedule.rules,
			tables,
			reservations,
			timezone,
			options?.partySize,
			combinations,
			restaurantTimezone,
			lastReservationTime,
			history,
		)
	}

	/**
	 * Maps the client-provided instant onto UTC midnight of the calendar day the
	 * client meant.
	 *
	 * Exactly-UTC-midnight instants are treated as the canonical "UTC calendar
	 * day" form (what the current widget sends) — applying the client timezone
	 * to those would shift guests WEST of UTC to the previous day. Any other
	 * instant is a legacy local-midnight value and is resolved via the
	 * provided timezone (Berlin midnight = 22:00 UTC of the previous day).
	 */
	private normalizeToCalendarDay(date: Date, timezone?: string): Date {
		const isUtcMidnight =
			date.getUTCHours() === 0 &&
			date.getUTCMinutes() === 0 &&
			date.getUTCSeconds() === 0 &&
			date.getUTCMilliseconds() === 0
		if (isUtcMidnight || !timezone) {
			return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
		}
		const zoned = new TZDate(date, timezone)
		return new Date(Date.UTC(zoned.getFullYear(), zoned.getMonth(), zoned.getDate()))
	}

	/**
	 * Generates a list of time slots with their availabity status
	 * @param date The date for which time slots will be generated
	 * @param rules A list of {@link TimingRule} to calculate time slots
	 * @param tableCount Total count of tables available in restaurant. Used to calculate if a time slot is available
	 * @param reservations A list of reservations to verify if a time slot has a reservation
	 * @param timezone Optional timezone to convert timeslots to local time
	 * @returns A list of {@link TimeSlot}
	 */
	private generateTimeSlots(
		date: Date,
		rules: TimingRule[],
		tables: Table[],
		reservations: Reservation[],
		timezone?: string,
		partySize?: number,
		combinations: TableCombination[] = [],
		restaurantTimezone?: string,
		lastReservationTime?: ITime,
		history: Reservation[] = [],
	): TimeSlot[] {
		const tableCount = tables.length
		const timeSlots: TimeSlot[] = []
		// Demand score per RETURNED slot, parallel to timeSlots; the peak
		// reference additionally tracks every cutoff-eligible time of the day
		// (see below), so the popular verdict is decided in a second pass.
		const demandScores: number[] = []
		let poolPeakScore = 0

		// How many tables/combinations can seat this party size at all — needed to
		// tell "booked away by guests" apart from "there was only ever one option".
		const totalForPartySize = partySize ? (getAllPartySizes(tables, combinations).get(partySize) ?? 0) : undefined

		const utcCurrentDate = new TZDate().withTimeZone('UTC')

		for (const [shiftIndex, rule] of rules.entries()) {
			let startingTime = set(date, { ...rule.openingTime })
			let endingTime = set(date, { ...rule.closingTime })
			const shiftOpening = set(date, { ...rule.openingTime })
			// Indices (into timeSlots) of this shift's tip candidates
			const tipCandidates: number[] = []

			// If closingTime is less than opening time, this means shift ends in next day
			if (TimeUtils.isBefore(rule.closingTime, rule.openingTime)) {
				endingTime = addDays(endingTime, 1)
			}

			while (isBefore(startingTime, endingTime) || isEqual(startingTime, endingTime)) {
				// Find reservations in the same time interval
				const existingReservations = reservations.filter((reservation) =>
					areIntervalsOverlapping(
						{ start: reservation.startDate, end: reservation.endDate },
						{ start: startingTime, end: addMinutes(startingTime, 30) },
					),
				)

				const occupiedTables = uniqBy(
					flatMap(existingReservations, (res) => res.tables).filter(removeNulls),
					(table) => table.id,
				)

				const startingTimeLocal = TimeUtils.utcToLocal(
					{ hours: startingTime.getHours(), minutes: startingTime.getMinutes() },
					timezone,
				)

				// Past/too-soon times are not offered at all (a struck-through chip
				// should always mean "occupied", never "already over")
				const isBookable = !isBefore(startingTime, addMinutes(utcCurrentDate, MIN_LEAD_MINUTES))

				// Optional per-restaurant cutoff: no reservation starts after this local time
				const isBeforeCutoff =
					!lastReservationTime || !this.isAfterLocalTime(startingTime, lastReservationTime, restaurantTimezone)

				if (isBeforeCutoff) {
					// The peak-reference pool covers EVERY cutoff-eligible time of the
					// day — including times already past for today. isBookable only
					// filters what is displayed; letting it shrink the pool would make
					// "popular" drift with the wall clock (a slot could turn popular in
					// the afternoon merely because the true lunch peak left the pool).
					const demandScore = this.historicalDemandScore(startingTime, history, utcCurrentDate, restaurantTimezone)
					if (demandScore > poolPeakScore) poolPeakScore = demandScore

					if (isBookable) {
						// With a party size the capacity check shares the Google availability
						// source of truth (getOpenTableCount); without one, clients keep the
						// coarse any-table-free check.
						const openForPartySize = partySize
							? getOpenTableCount(tables, existingReservations, partySize, combinations)
							: undefined
						const hasCapacity =
							openForPartySize === undefined ? occupiedTables.length !== tableCount : openForPartySize > 0

						const occupancy = this.occupancyLevel(
							tableCount,
							occupiedTables.length,
							openForPartySize,
							totalForPartySize,
						)
						// Only genuinely quiet, bookable times can carry the house tip.
						// "Quiet" must also hold for the requested party size: more than
						// half of its seating options still open (the global ratio alone
						// can look calm while most tables fitting THIS group are gone).
						const quietForPartySize =
							openForPartySize === undefined ||
							totalForPartySize === undefined ||
							openForPartySize * 2 > totalForPartySize
						const minutesIntoShift = (startingTime.getTime() - shiftOpening.getTime()) / 60_000
						const inTipWindow =
							minutesIntoShift >= TIP_WINDOW_START_MINUTES && minutesIntoShift <= TIP_WINDOW_END_MINUTES

						timeSlots.push({
							available: hasCapacity,
							time: startingTimeLocal,
							occupancy,
							popular: false,
							recommended: false,
						})
						demandScores.push(demandScore)

						if (hasCapacity && occupancy === 'low' && quietForPartySize && inTipWindow) {
							tipCandidates.push(timeSlots.length - 1)
						}
					}
				}

				startingTime = addMinutes(startingTime, this.slotStepMinutes(startingTime, restaurantTimezone))
			}

			for (const index of this.pickTipIndices(tipCandidates, date, shiftIndex)) {
				timeSlots[index].recommended = true
			}
		}

		// Second pass: popular = within three quarters of the day's peak demand
		// (peak taken over the whole day's pool, so the verdict is stable no
		// matter when the guest asks), never below the minimum-evidence floor.
		// With no history all scores are zero and the floor keeps every flag false.
		const popularThreshold = Math.max(
			POPULAR_MIN_TABLE_WEEKS,
			Math.ceil((poolPeakScore * POPULAR_PEAK_SHARE_NUMERATOR) / POPULAR_PEAK_SHARE_DENOMINATOR),
		)
		for (const [index, slot] of timeSlots.entries()) {
			slot.popular = demandScores[index] >= popularThreshold
		}

		return timeSlots
	}

	/**
	 * Picks up to RECOMMENDED_PER_SHIFT distinct candidates, seeded from the
	 * calendar date and shift — deterministic per day (no flicker on reload),
	 * different across days (never looks static). No Math.random on purpose.
	 */
	private pickTipIndices(candidates: number[], date: Date, shiftIndex: number): number[] {
		if (candidates.length <= RECOMMENDED_PER_SHIFT) return candidates

		// djb2-xor over "YYYY-MM-DD#shift"…
		const input = `${date.toISOString().slice(0, 10)}#${shiftIndex}`
		let seed = 5381
		for (let i = 0; i < input.length; i++) {
			seed = (Math.imul(seed, 33) ^ (input.codePointAt(i) ?? 0)) >>> 0
		}
		// …plus a murmur3-style finalizer: raw djb2 barely changes for
		// consecutive date strings, which made the pick streaky within a
		// ten-day stretch (reviewer-measured). The avalanche breaks that.
		seed = Math.imul(seed ^ (seed >>> 16), 2_246_822_507) >>> 0
		seed = Math.imul(seed ^ (seed >>> 13), 3_266_489_909) >>> 0
		seed = (seed ^ (seed >>> 16)) >>> 0

		const count = candidates.length
		const first = seed % count
		const offset = 1 + (Math.floor(seed / count) % (count - 1))
		const second = (first + offset) % count
		return [candidates[first], candidates[second]]
	}

	/**
	 * Demand score of this weekday+time over the past weeks: unique booked
	 * tables per lookback window, summed. Weeks without bookings (or closed
	 * days) contribute zero, so thin history honestly stays a low score.
	 *
	 * Windows anchored to a far-future target day can land AFTER now — those are
	 * not history yet and contribute zero. Without this guard, other guests'
	 * confirmed future bookings would masquerade as "popular" evidence, which is
	 * exactly the fabricated-demand signal this feature must never emit.
	 */
	private historicalDemandScore(
		slotStart: Date,
		history: Reservation[],
		now: Date,
		restaurantTimezone?: string,
	): number {
		if (history.length === 0) return 0

		let occupiedTableWeeks = 0
		for (let week = 1; week <= POPULAR_LOOKBACK_WEEKS; week++) {
			// Subtract calendar days in the restaurant's timezone so the wall-clock
			// time stays stable across DST changes within the lookback window.
			const base = restaurantTimezone ? new TZDate(slotStart, restaurantTimezone) : slotStart
			const windowStart = subDays(base, week * 7)
			const windowEnd = addMinutes(windowStart, 30)

			// Only fully elapsed windows are history
			if (isBefore(now, windowEnd)) continue

			const occupiedTables = uniqBy(
				history
					.filter((reservation) =>
						areIntervalsOverlapping(
							{ start: reservation.startDate, end: reservation.endDate },
							{ start: windowStart, end: windowEnd },
						),
					)
					.flatMap((reservation) => reservation.tables ?? [])
					.filter(removeNulls),
				(table) => table.id,
			)
			occupiedTableWeeks += occupiedTables.length
		}

		return occupiedTableWeeks
	}

	/**
	 * Demand signal for a slot, derived only from real bookings — the widget must
	 * never show fabricated demand (misleading-scarcity claims are actionable).
	 *
	 * "high" needs either a mostly-booked restaurant or the LAST remaining option
	 * for the requested party size — the latter only when at least two options
	 * exist at all, so structural scarcity (a size only one table or combination
	 * can ever seat) never masquerades as demand.
	 */
	private occupancyLevel(
		tableCount: number,
		occupiedCount: number,
		openForPartySize?: number,
		totalForPartySize?: number,
	): SlotOccupancy {
		if (tableCount === 0) return 'low'
		const lastOptionBookedAway =
			totalForPartySize !== undefined && totalForPartySize >= 2 && openForPartySize === 1
		const occupiedRatio = occupiedCount / tableCount
		if (occupiedRatio >= HIGH_OCCUPANCY_RATIO || lastOptionBookedAway) return 'high'
		if (occupiedRatio >= MEDIUM_OCCUPANCY_RATIO) return 'medium'
		return 'low'
	}

	/** True when the slot's restaurant-local wall time is after the given time. */
	private isAfterLocalTime(slotStart: Date, cutoff: ITime, restaurantTimezone?: string): boolean {
		const local = restaurantTimezone ? new TZDate(slotStart, restaurantTimezone) : slotStart
		return (
			local.getHours() > cutoff.hours || (local.getHours() === cutoff.hours && local.getMinutes() > cutoff.minutes)
		)
	}

	/**
	 * 15-minute grid up to (excluding) 20:15 restaurant-local time, 30 minutes
	 * afterwards — finer choice in the busy window without flooding the late UI.
	 */
	private slotStepMinutes(slotStart: Date, restaurantTimezone?: string): number {
		const local = restaurantTimezone ? new TZDate(slotStart, restaurantTimezone) : slotStart
		const isEvening =
			local.getHours() > COARSE_GRID_FROM.hours ||
			(local.getHours() === COARSE_GRID_FROM.hours && local.getMinutes() >= COARSE_GRID_FROM.minutes)
		// Overnight shifts must not fall back to the fine grid after midnight
		const isNight = local.getHours() < COARSE_GRID_UNTIL_HOUR
		return isEvening || isNight ? COARSE_STEP_MINUTES : FINE_STEP_MINUTES
	}
}
